import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Select from '@radix-ui/react-select';
import { useDomainStore, useMemberAccessStore } from '@/hooks/useStores';
import { canCreateMemberBlocks, getAssignableRoles, getRoleDisplayName, type UserRole } from '@/utils/rolePermissions';
import {
  familyHasPaidPlan,
  getFamilyPlanUpgradeHint,
  getUpgradeAwareErrorParts,
  isFamilyOwner
} from '@/utils/familyPlan';
import type { Role } from '@/types/member-access';
import { WeTreeApiError } from '@/api/errors';
import Spinner from '@/components/shared/Spinner';
import PlanUpgradeAlert from '@/components/shared/PlanUpgradeAlert';
import UpgradeFamilyPlanModal from '@/components/family/UpgradeFamilyPlanModal';
import '@/styles/form-select.css';
import './manage-member-access-modal.css';

type TabId = 'roles' | 'blocks';

interface ManageMemberAccessModalProps {
  familyId: string;
  familyMemberId: string;
  memberLabel?: string;
  /** Whether the viewer may manage access for this member (effective owner/admin on them). */
  canManage: boolean;
  /** The viewer's effective role on this member, which caps the roles they may assign. */
  actorEffectiveRole: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const ManageMemberAccessModal: React.FC<ManageMemberAccessModalProps> = observer(
  ({ familyId, familyMemberId, memberLabel: memberLabelProp, canManage, actorEffectiveRole, isOpen, onClose }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const memberLabel = memberLabelProp ?? t('memberAccess.thisMember');
    const domainStore = useDomainStore();
    const accessStore = useMemberAccessStore();
    const plan = domainStore.currentFamily?.plan;
    const canCreateBlocks = canCreateMemberBlocks(plan);
    const isOwner = isFamilyOwner(domainStore.currentFamily?.userRole);
    const roleOptions = getAssignableRoles(actorEffectiveRole);

    const [tab, setTab] = useState<TabId>('roles');
    const [addUserId, setAddUserId] = useState('');
    const [addRole, setAddRole] = useState<Role>('viewer');
    const [blockUserId, setBlockUserId] = useState('');
    const [blockReason, setBlockReason] = useState('');
    const [blockIncludeDescendants, setBlockIncludeDescendants] = useState(false);
    const [upgradeHint, setUpgradeHint] = useState<string | null>(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
      if (!isOpen || !canManage) return;
      setTab('roles');
      setLocalError(null);
      setUpgradeHint(null);
      setAddUserId('');
      setAddRole((roleOptions[roleOptions.length - 1] as Role) || 'viewer');
      setBlockUserId('');
      setBlockReason('');
      setBlockIncludeDescendants(false);
      void accessStore.loadMemberAccess(familyId, familyMemberId);
      return () => {
        accessStore.resetMemberTarget();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when modal opens for this member
    }, [isOpen, familyId, familyMemberId, canManage]);

    if (!isOpen) return null;

    if (!canManage) {
      return (
        <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
          <div className="v01-modal manage-member-access-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-member-access-modal__header">
              <h2 className="v01-modal__title">{t('memberAccess.title')}</h2>
              <button type="button" onClick={onClose} className="v01-modal__close" aria-label={tCommon('close')}>
                ×
              </button>
            </div>
            <p className="manage-member-access-modal__lede">
              {t('memberAccess.cannotManage', { member: memberLabel })}
            </p>
            <div className="v01-modal__footer">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {tCommon('close')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    const availableForRoles = accessStore.availableUsersForRoles();
    const availableForBlocks = accessStore.availableUsersForBlocks();
    const displayError = localError || accessStore.error;

    const handleAddRole = async () => {
      if (!addUserId) return;
      setLocalError(null);
      setUpgradeHint(null);
      try {
        await accessStore.createMemberRoleOverride({
          userId: addUserId,
          role: addRole
        });
        setAddUserId('');
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('memberAccess.failedAddOverride'));
      }
    };

    const handleRoleChange = async (userId: string, role: string) => {
      setLocalError(null);
      try {
        await accessStore.updateMemberRoleOverride(userId, { role: role as Role });
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('memberAccess.failedUpdateRole'));
      }
    };

    const handleRemoveRole = async (userId: string) => {
      if (!window.confirm(t('confirm.removeOverride'))) {
        return;
      }
      setLocalError(null);
      try {
        await accessStore.deleteMemberRoleOverride(userId);
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('memberAccess.failedRemoveOverride'));
      }
    };

    const handleAddBlock = async () => {
      if (!blockUserId) return;
      setLocalError(null);
      setUpgradeHint(null);
      if (!canCreateBlocks) {
        setLocalError(t('memberAccess.blocksRequireFamilyPlan'));
        setUpgradeHint(getFamilyPlanUpgradeHint(isOwner));
        return;
      }
      try {
        await accessStore.createMemberBlock({
          userId: blockUserId,
          includeDescendants: blockIncludeDescendants,
          reason: blockReason
        });
        setBlockUserId('');
        setBlockReason('');
        setBlockIncludeDescendants(false);
      } catch (err) {
        if (err instanceof WeTreeApiError && err.isUpgradeRequired()) {
          const parts = getUpgradeAwareErrorParts(err, isOwner, t('memberAccess.failedCreateBlock'));
          setLocalError(parts.message);
          setUpgradeHint(parts.upgradeHint);
        } else {
          setLocalError(err instanceof Error ? err.message : t('memberAccess.failedCreateBlock'));
        }
      }
    };

    const handleToggleBlockDescendants = async (userId: string, includeDescendants: boolean) => {
      setLocalError(null);
      try {
        await accessStore.updateMemberBlock(userId, { includeDescendants });
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('memberAccess.failedUpdateBlock'));
      }
    };

    const handleRemoveBlock = async (userId: string) => {
      if (!window.confirm(t('confirm.removeBlock'))) {
        return;
      }
      setLocalError(null);
      try {
        await accessStore.deleteMemberBlock(userId);
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : t('memberAccess.failedRemoveBlock'));
      }
    };

    return (
      <>
        <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
          <div className="v01-modal manage-member-access-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-member-access-modal__header">
              <h2 className="v01-modal__title">{t('memberAccess.title')}</h2>
              <button type="button" onClick={onClose} className="v01-modal__close" aria-label={tCommon('close')}>
                ×
              </button>
            </div>

            <p className="manage-member-access-modal__lede">{t('memberAccess.lede', { member: memberLabel })}</p>

            <div className="manage-member-access-modal__tabs" role="tablist" aria-label={t('memberAccess.tabsAria')}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'roles'}
                className={`manage-member-access-modal__tab${tab === 'roles' ? ' manage-member-access-modal__tab--active' : ''}`}
                onClick={() => setTab('roles')}
              >
                {t('memberAccess.tabRoles')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'blocks'}
                className={`manage-member-access-modal__tab${tab === 'blocks' ? ' manage-member-access-modal__tab--active' : ''}`}
                onClick={() => setTab('blocks')}
              >
                {t('memberAccess.tabBlocks')}
              </button>
            </div>

            <div className="manage-member-access-modal__body">
              {accessStore.isLoading ? (
                <Spinner message={t('memberAccess.loading')} />
              ) : (
                <>
                  {displayError && (
                    <PlanUpgradeAlert
                      message={displayError}
                      upgradeHint={upgradeHint}
                      onDismiss={() => {
                        setLocalError(null);
                        setUpgradeHint(null);
                        accessStore.clearError();
                      }}
                    />
                  )}

                  {tab === 'roles' && (
                    <div role="tabpanel">
                      <p className="manage-member-access-modal__section-lede">
                        {t('memberAccess.rolesLede', { member: memberLabel })}
                      </p>

                      {accessStore.roleAssignments.length === 0 ? (
                        <p className="manage-member-access-modal__empty">{t('memberAccess.noOverrides')}</p>
                      ) : (
                        <ul className="manage-member-access-modal__list">
                          {accessStore.roleAssignments.map((assignment) => (
                            <li key={assignment.userId} className="manage-member-access-modal__item">
                              <div className="manage-member-access-modal__identity">
                                <span className="manage-member-access-modal__name">
                                  {accessStore.userLabel(assignment.userId)}
                                </span>
                              </div>
                              <div className="manage-member-access-modal__controls">
                                <Select.Root
                                  value={assignment.role}
                                  onValueChange={(value) => {
                                    void handleRoleChange(assignment.userId, value);
                                  }}
                                  disabled={accessStore.isSaving}
                                >
                                  <Select.Trigger
                                    className="form-select-trigger manage-member-access-modal__select"
                                    aria-label={t('memberAccess.roleForAria', {
                                      name: accessStore.userLabel(assignment.userId)
                                    })}
                                  >
                                    <Select.Value />
                                  </Select.Trigger>
                                  <Select.Portal>
                                    <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                                      {roleOptions.map((role) => (
                                        <Select.Item key={role} value={role} className="form-select-item">
                                          <Select.ItemText>{getRoleDisplayName(role as UserRole)}</Select.ItemText>
                                        </Select.Item>
                                      ))}
                                    </Select.Content>
                                  </Select.Portal>
                                </Select.Root>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  disabled={accessStore.isSaving}
                                  onClick={() => {
                                    void handleRemoveRole(assignment.userId);
                                  }}
                                >
                                  {tCommon('remove')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}

                      {availableForRoles.length > 0 && (
                        <div className="manage-member-access-modal__add">
                          <h3 className="manage-member-access-modal__add-title">
                            {t('memberAccess.addOverrideTitle')}
                          </h3>
                          <div className="manage-member-access-modal__add-row">
                            <Select.Root value={addUserId || undefined} onValueChange={setAddUserId}>
                              <Select.Trigger
                                className="form-select-trigger manage-member-access-modal__select"
                                aria-label={t('memberAccess.userAria')}
                              >
                                <Select.Value placeholder={t('memberAccess.selectUser')} />
                              </Select.Trigger>
                              <Select.Portal>
                                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                                  {availableForRoles.map((user) => (
                                    <Select.Item key={user.userId} value={user.userId} className="form-select-item">
                                      <Select.ItemText>
                                        {user.memberName ||
                                          user.nickname ||
                                          user.email ||
                                          t('memberAccess.userFallback')}
                                      </Select.ItemText>
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Portal>
                            </Select.Root>
                            <Select.Root value={addRole} onValueChange={(v) => setAddRole(v as Role)}>
                              <Select.Trigger
                                className="form-select-trigger manage-member-access-modal__select"
                                aria-label={t('memberAccess.roleAria')}
                              >
                                <Select.Value />
                              </Select.Trigger>
                              <Select.Portal>
                                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                                  {roleOptions.map((role) => (
                                    <Select.Item key={role} value={role} className="form-select-item">
                                      <Select.ItemText>{getRoleDisplayName(role as UserRole)}</Select.ItemText>
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Portal>
                            </Select.Root>
                          </div>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            disabled={!addUserId || accessStore.isSaving}
                            onClick={() => {
                              void handleAddRole();
                            }}
                          >
                            {t('actions.addOverride')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {tab === 'blocks' && (
                    <div role="tabpanel">
                      <p className="manage-member-access-modal__section-lede">{t('memberAccess.blocksLede')}</p>

                      {!familyHasPaidPlan(plan) && (
                        <PlanUpgradeAlert
                          message={t('memberAccess.blocksRequirePlanBanner')}
                          upgradeHint={getFamilyPlanUpgradeHint(isOwner)}
                        />
                      )}

                      {accessStore.blocks.length === 0 ? (
                        <p className="manage-member-access-modal__empty">{t('memberAccess.noBlocks')}</p>
                      ) : (
                        <ul className="manage-member-access-modal__list">
                          {accessStore.blocks.map((block) => (
                            <li key={block.userId} className="manage-member-access-modal__item">
                              <div className="manage-member-access-modal__identity">
                                <span className="manage-member-access-modal__name">
                                  {accessStore.userLabel(block.userId)}
                                </span>
                                {block.reason && (
                                  <span className="manage-member-access-modal__meta">{block.reason}</span>
                                )}
                                <label className="manage-member-access-modal__check">
                                  <input
                                    type="checkbox"
                                    checked={block.includeDescendants}
                                    disabled={accessStore.isSaving}
                                    onChange={(e) => {
                                      void handleToggleBlockDescendants(block.userId, e.target.checked);
                                    }}
                                  />
                                  {t('memberAccess.includeDescendants')}
                                </label>
                              </div>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                disabled={accessStore.isSaving}
                                onClick={() => {
                                  void handleRemoveBlock(block.userId);
                                }}
                              >
                                {tCommon('remove')}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      {availableForBlocks.length > 0 && (
                        <div className="manage-member-access-modal__add">
                          <h3 className="manage-member-access-modal__add-title">{t('memberAccess.addBlockTitle')}</h3>
                          <Select.Root value={blockUserId || undefined} onValueChange={setBlockUserId}>
                            <Select.Trigger
                              className="form-select-trigger manage-member-access-modal__select"
                              aria-label={t('memberAccess.userToBlockAria')}
                            >
                              <Select.Value placeholder={t('memberAccess.selectUser')} />
                            </Select.Trigger>
                            <Select.Portal>
                              <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                                {availableForBlocks.map((user) => (
                                  <Select.Item key={user.userId} value={user.userId} className="form-select-item">
                                    <Select.ItemText>
                                      {user.memberName || user.nickname || user.email || t('memberAccess.userFallback')}
                                    </Select.ItemText>
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Portal>
                          </Select.Root>
                          <label className="manage-member-access-modal__field">
                            <span className="manage-member-access-modal__field-label">
                              {t('memberAccess.reasonOptional')}
                            </span>
                            <input
                              type="text"
                              className="manage-member-access-modal__input"
                              value={blockReason}
                              onChange={(e) => setBlockReason(e.target.value)}
                              maxLength={500}
                              placeholder={t('memberAccess.reasonPlaceholder')}
                            />
                          </label>
                          <label className="manage-member-access-modal__check">
                            <input
                              type="checkbox"
                              checked={blockIncludeDescendants}
                              onChange={(e) => setBlockIncludeDescendants(e.target.checked)}
                            />
                            {t('memberAccess.includeDescendantsBranch')}
                          </label>
                          <div className="manage-member-access-modal__add-actions">
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm"
                              disabled={!blockUserId || accessStore.isSaving}
                              onClick={() => {
                                void handleAddBlock();
                              }}
                            >
                              {t('actions.addBlock')}
                            </button>
                            {!canCreateBlocks && isOwner && (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setShowUpgradeModal(true)}
                              >
                                {t('actions.upgradePlan')}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="v01-modal__footer">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {tCommon('close')}
              </button>
            </div>
          </div>
        </div>

        {showUpgradeModal && (
          <UpgradeFamilyPlanModal
            familyId={familyId}
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
          />
        )}
      </>
    );
  }
);

export default ManageMemberAccessModal;
